import React, { useState, useEffect, useCallback } from 'react';
import { gallery } from '../../portal/api';
import { setGalleryPresent } from './galleryPresence';
import './GallerySection.css';

/**
 * Public gallery, fed by whatever the owner has uploaded through his portal.
 *
 * If the API is unreachable or nothing has been published yet, the section
 * removes itself entirely rather than leaving an empty hole on the homepage —
 * a brochure site should never show a visitor a loading skeleton that never
 * resolves.
 */
export default function GallerySection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => {
    let active = true;
    gallery
      .list()
      .then((result) => {
        if (!active) return;
        const published = result.items || [];
        setItems(published);
        setLoading(false);
        setGalleryPresent(published.length > 0);
      })
      .catch(() => {
        if (!active) return;
        setItems([]);
        setLoading(false);
        setGalleryPresent(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const close = useCallback(() => setLightboxIndex(-1), []);
  const step = useCallback(
    (delta) => {
      setLightboxIndex((current) => {
        if (current < 0) return current;
        const next = current + delta;
        if (next < 0) return items.length - 1;
        if (next >= items.length) return 0;
        return next;
      });
    },
    [items.length]
  );

  useEffect(() => {
    if (lightboxIndex < 0) return undefined;

    const onKey = (event) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };

    document.addEventListener('keydown', onKey);
    // Stop the page behind the lightbox scrolling under it on mobile.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [lightboxIndex, close, step]);

  if (loading || items.length === 0) {
    return null;
  }

  const active = lightboxIndex >= 0 ? items[lightboxIndex] : null;

  return (
    <section id="gallery" className="gallery">
      <div className="gallery-inner">
        <div className="gallery-eyebrow">Recent Work</div>
        <h2 className="gallery-title">Fresh From the Job Site</h2>
        <p className="gallery-subtitle">
          Photos straight from my most recent jobs. Tap any picture to take a closer look.
        </p>

        <div className="gallery-grid">
          {items.map((item, index) => (
            <button
              type="button"
              className="gallery-card"
              key={item.id}
              onClick={() => setLightboxIndex(index)}
              aria-label={'View ' + (item.title || 'photo') + ' larger'}
            >
              <img src={item.thumb_url} alt={item.title || 'Completed project'} loading="lazy" />
              {(item.title || item.category) && (
                <span className="gallery-card-label">
                  {item.title}
                  {item.category && <em>{item.category}</em>}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {active && (
        <div className="gallery-lightbox" role="dialog" aria-modal="true" onClick={close}>
          <button className="gallery-close" type="button" onClick={close} aria-label="Close">
            ×
          </button>

          {items.length > 1 && (
            <button
              className="gallery-nav gallery-prev"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                step(-1);
              }}
              aria-label="Previous photo"
            >
              ‹
            </button>
          )}

          <figure className="gallery-figure" onClick={(e) => e.stopPropagation()}>
            <img src={active.url} alt={active.title || 'Completed project'} />
            {(active.title || active.caption) && (
              <figcaption>
                {active.title && <strong>{active.title}</strong>}
                {active.caption && <span>{active.caption}</span>}
              </figcaption>
            )}
          </figure>

          {items.length > 1 && (
            <button
              className="gallery-nav gallery-next"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                step(1);
              }}
              aria-label="Next photo"
            >
              ›
            </button>
          )}
        </div>
      )}
    </section>
  );
}
